import MemberDetails from './MemberDetails';
import Overview from './Overview';
import { useTranslation } from 'react-i18next';
import { Suspense, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MyPlanTabs,
  convertToDate,
  useAnalytics,
} from 'common';
import ApiWrapper from '../../common/ApiWrapper';
import { GlobalContext, isAvMed } from 'context';
import { transformOverviewData } from '../utils/OverviewDataTransformer';

import {
  Headings,
  Disclaimer,
  ButtonTabs,
  Loader,
} from '@sentara-health/optimahealth_web';
import {
  useConsolidateSUmmary,
  MyConsolidatedResponse,
  useMemberReadv4,
} from '@sentara-health/sentara-api-hooks-core';

function MyPlan() {
  const { t } = useTranslation();
  const {
    getFeature,
    age,
    loginMemberId,
    setIsMemberTabDefault,
    isDesignatedTabShow,
  } = useContext(GlobalContext);
  const formattedVal = convertToDate(new Date());
  const [, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const { ConsolidateSUmmary } = useConsolidateSUmmary();
  const {
    data: getConsolidatedData,
    error: getConsolidatedError,
    callApi: getConsolidated,
  }: any = ApiWrapper(ConsolidateSUmmary);
  const [myConsResState, setMyConsResState] = useState<
    MyConsolidatedResponse | []
  >([]);
  const hasInitialized = useRef(false);
  const { trackContentListing } = useAnalytics();

  const trackMyPlanAdobeEvent = (
    contentId: string,
    contentTitle: string,
    itemPosition: number
  ) => {
    trackContentListing(
      contentId,
      contentTitle,
      itemPosition
    );
  };

  const { fetchMemberDetails } = useMemberReadv4();

  // Switching the tabs for memberdetails and overview tabs
  const handleTabClick = (tabId: string) => {
    if (tabId === MyPlanTabs.designatedTabs) {
      setSearchParams({ id: MyPlanTabs.designatedID });
    } else if (tabId === MyPlanTabs.memberDetailsTabs) {
      trackMyPlanAdobeEvent('703', 'My_plan_member_details_click', 3);
      setSearchParams({ id: MyPlanTabs.memberDetailID });
    } else {
      trackMyPlanAdobeEvent('702', 'My_plan_overview_click', 2);
      setSearchParams({ id: MyPlanTabs.overViewID });
    }
  };

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      setLoading(true);
      if (isAvMed()) {
        overView();
      } else {
        getConsolidated('1');
      }
    }
  }, [hasInitialized]);

  async function overView() {
    const memberData = {
      identifier: [
        {
          system: 'MemberId',
          value: loginMemberId,
        },
      ],
      retriveMemberAccumulations: true,
    };
    const memberResponse = await fetchMemberDetails(memberData);

    if (memberResponse?.data?.accumulations) {
      try {
        const transformedData = transformOverviewData(
          memberResponse?.data?.accumulations
        );

        if (transformedData && transformedData.length > 0) {
          setIsMemberTabDefault(false);
          if (!isDesignatedTabShow) {
            setSearchParams({ id: MyPlanTabs.overViewID });
          }
          setMyConsResState(transformedData as MyConsolidatedResponse);
        }
      } catch (error) {
        console.error('Error parsing accumulations data:', error);
      }
    } else {
      setIsMemberTabDefault(true);
      if (!isDesignatedTabShow) {
        setSearchParams({ id: MyPlanTabs.memberDetailID });
      }
    }

    setLoading(false);
  }

  useEffect(() => {
    if (getConsolidatedData === '') {
      setLoading(false);
      setIsMemberTabDefault(true);
      if (!isDesignatedTabShow) {
        setSearchParams({ id: MyPlanTabs.memberDetailID });
      }
    } else if (getConsolidatedData?.length) {
      setLoading(false);
      setIsMemberTabDefault(false);
      if (!isDesignatedTabShow) {
        setSearchParams({ id: MyPlanTabs.overViewID });
      }
      setMyConsResState(getConsolidatedData);
    }

    if (getConsolidatedError) {
      setLoading(false);
    }
  }, [getConsolidatedData, getConsolidatedError]);

  // getting the params Id from the url
  const search = window.location.search;
  const newParams = new URLSearchParams(search);
  const param = newParams.get('id');

  //To display the overView and memberdetail tab
  const tabsData = [
    {
      content: (
        <Suspense fallback={<div>Loading...</div>}>
          {param === MyPlanTabs.overViewID && (
            <>
              <Overview myConsResState={myConsResState} />
              <DisclaimerMessage t={t} formattedVal={formattedVal} />
            </>
          )}
        </Suspense>
      ),
      id: MyPlanTabs.overviewTabs,
      label: t('yourPlan.overview'),
    },
    {
      content: (
        <Suspense fallback={<div>Loading...</div>}>
          {param === MyPlanTabs.memberDetailID && (
            <>
              <MemberDetails />
              <DisclaimerMessage t={t} formattedVal={formattedVal} />
            </>
          )}
        </Suspense>
      ),
      id: MyPlanTabs.memberDetailsTabs,
      label: t('yourPlan.memberDetails'),
    },
  ];


  return (
    <>
      {loading && <Loader />}

      <Headings
        level={1}
        text={t('common.myPlan')}
        variant="semiBold"
        dataTestId="my_plan_header"
        className="mb-4"
      />
      {getFeature && Object.keys(getFeature).length > 0 && !loading && (
        <div className="homeAddress">
          <ButtonTabs
            tabIdHandler={handleTabClick}
            tabs={tabsData}
          />
        </div>
      )}
    </>
  );
}

export default MyPlan;

const DisclaimerMessage = ({ t, formattedVal }: any) => {
  return (
    <div className="pb-4 mb-2">
      <Disclaimer
        disclaimerTitle={t('yourPlan.readDisclaimer')}
        disclaimerContent={`${t(
          'common.disclaimerMsg1'
        )} ${formattedVal}. ${t('common.disclaimerMsg2')}`}
      />
    </div>
  );
};
